import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { PowerType } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { z } from 'zod'

import { convertAreaLabel } from '~/models/area'
import { BG_CODE_FORMAT } from '~/models/bgCode'
import {
  type CivilDate,
  compareCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { licenseTypeLabel } from '~/models/licenseType'
import { areaCodeFromBgCode } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/areaCodeFromBgCode'
import { addOwnerMemberIssues } from '~/pages/AdminOcctoMaster/tabs/_common/models/ownerMembers'
import { uuidString } from '~/pages/AdminOcctoMaster/tabs/_common/models/uuidString'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'
import {
  validPeriodRefine,
  validPeriodRefineIssue,
  withinRunes,
} from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * licenseType ごとに要求される BG コード先頭文字。
 * backend の validateBgMasterCodeLicenseAlignment と一致させる:
 *   G**** ⇔ 発電 / D**** ⇔ DR / L**** ⇔ 小売 or 自己託送。
 */
export const REQUIRED_BG_CODE_PREFIX: Record<number, string> = {
  [LicenseType.GENERATION]: 'G',
  [LicenseType.DR]: 'D',
  [LicenseType.RETAIL]: 'L',
  [LicenseType.SELF_WHEELING]: 'L',
}

/**
 * contract_start_date / BgMember の有効期間 (validFrom / validTo) 共通の
 * 日付 schema。
 */
const dateSchema = z.object({
  year: z.number().int().min(1900).max(2100),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
})

// dateSchema の出力形 ({ year, month, day }) は `~/models/civilDate` の
// `CivilDate` と構造的に同一なので、 その import をそのまま型として使う
// (member 行 / BG 本体の適用期間比較で共通利用)。

/**
 * BG 構成事業者 input schema。
 * validFrom（有効開始日）は必須、 validTo（有効終了日）は任意
 * (spec bg.md L25。 デフォルトは呼び出し側 (OwnerMembersSection) が
 * ValidFrom=今日 / ValidTo=なしで埋める)。
 *
 * 行単体の to>=from (同日許容) を validPeriodRefine で検証する
 * (occto-master-bg-member-valid-period-containment 設計書 Scope: 従来 BG 本体
 * にのみ適用されていた行単体の期間検証を member 行にも適用する)。 BG 本体
 * 期間との内包制約 (AC-OMB-020) は buildBgMasterFormSchema の
 * superRefine 側で検証する (= data.validFrom/validTo との cross-field
 * 検証のため member 行単体の schema では扱えない)。
 */
const BgMemberInputSchema = z
  .object({
    businessMasterId: uuidString('事業者を選択してください'),
    isOwn: z.boolean(),
    validFrom: dateSchema,
    // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
    // clearAsNull 参照)。`null` を許容し `undefined` へ正規化して、以降の
    // 内包判定・validPeriodRefine・送信は従来どおり `undefined = 無期限` で扱う。
    // `.optional()` を末尾に付けて object key の optional 性 (省略可) を保つ。
    validTo: dateSchema
      .nullable()
      .transform((v) => v ?? undefined)
      .optional(),
  })
  .refine(validPeriodRefine, validPeriodRefineIssue)

export type BgMemberInput = z.infer<typeof BgMemberInputSchema>

/**
 * 配下発電所 (plant membership) input schema。
 * validFrom（有効開始日）は必須、 validTo（有効終了日）は任意
 * (design doc §5「entity」)。 BG 区分ゲート (発電以外は空配列) は
 * BgMasterFormSchema.superRefine 側で検証する。
 */
const BgPlantMembershipInputSchema = z.object({
  plantMasterId: uuidString('発電所を選択してください'),
  validFrom: dateSchema,
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化する
  // (BgMemberInputSchema.validTo と同パターン)。
  validTo: dateSchema
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

export type BgPlantMembershipInput = z.infer<
  typeof BgPlantMembershipInputSchema
>

/**
 * BG コードの形式チェック: 5 文字、先頭が G/L/D、2 文字目は英字、末尾が数字。
 * 区分 G/L/D いずれも 2 文字目は英字、3〜4 文字目は数字と紛らわしい I / O を
 * 除外する (OCCTO 採番体系 bgcode_saibanryouikikirikae_chuuikanki.pdf、
 * spec AC-OMB-002、issue #6279)。backend valueobject.ParseBgCode と同一。
 * entity 側での G/L/D prefix と licenseType の整合チェックは backend でのみ行う。
 */
const bgCodeSchema = z
  .string()
  .min(5, 'BG コードは 5文字で入力してください (例: GBBC2)')
  .max(5, 'BG コードは 5文字で入力してください (例: GBBC2)')
  .regex(
    BG_CODE_FORMAT,
    'BG コードは「G / L / D + 英字 + 英数字2文字 + エリアコード」で入力してください。I・O は使えません (例: GA0C2)',
  )

/**
 * BG マスタの base フォームフィールド (= licenseType に依存しない部分)。
 */
const BaseBgMasterFormSchema = z.object({
  bgName: z
    .string()
    .min(1, 'BG 名称を入力してください')
    .refine(withinRunes(30), 'BG 名称は30文字以内で入力してください'),
  repBusinessMasterId: uuidString('代表事業者を選択してください'),
  members: z
    .array(BgMemberInputSchema)
    .min(1, '所属事業者を1件以上指定してください'),
  // 配下発電所リスト (design doc §5)。 BG 区分=発電のときのみ非空を許容
  // (= superRefine で検証、 BgPlantMembershipSection が区分切替時に自動で
  // クリアするため通常は到達しないが、 backend の validateBgMasterPlants
  // ゲートを frontend でも defensive に mirror する)。
  plants: z.array(BgPlantMembershipInputSchema).default([]),
  wheelingLinkCode: z
    .string()
    .regex(
      /^[A-Z0-9]{5}$/,
      '電源特定コードは半角英数字5桁 (大文字) で入力してください',
    )
    .optional(),
  // 適用開始日 (必須) / 適用終了日 (任意)。 旧 contractStartDate は
  // occto-master-valid-period-6tabs 設計書により validFrom へ統合・廃止された
  // (AC-10)。
  validFrom: dateSchema,
  validTo: dateSchema.optional(),
  // 備考は説明・メモ系の既定値 100 文字 (guidelines/input-character-limits.md)。
  // TODO(#11320): proto の note に max_len 100 が無く、API 側の上限は未設定。
  note: z
    .string()
    .refine(withinRunes(100), '備考は100文字以内で入力してください')
    .optional(),
})

/**
 * BG マスタの Create / Edit 共通 Zod schema (discriminated union) を組み立てる。
 *
 * licenseType === GENERATION のとき powerType / contractIdNo1 が必須。
 * その他 (RETAIL / SELF_WHEELING / DR) では powerType / contractIdNo1 不要。
 * contractIdNo2 は BG マスタでは扱わない (spec bg.md L36: BG タブでは持たない。
 * AC-01)。
 *
 * superRefine で entity invariant も mirror:
 *  - bgCode の先頭文字と licenseType の整合 (= G/D/L prefix)
 *  - AC-BG14 (bgCode 末尾とareaCodeの不一致検証、 skipAreaMismatchCheck で無効化可)
 *  - メンバー検証 (is_own≤1 / 代表包含 / businessMasterId 重複不可) は PlantMaster と
 *    共通の addOwnerMemberIssues に委譲
 *
 * @param skipAreaMismatchCheck - true の場合 AC-BG14 の検証を行わない。
 *   Edit モードは bgCode / areaCode とも readonly (AC-006、
 *   BgMasterFormFields.isEdit の JSDoc 参照) で新規不整合は生じないため、
 *   pre-existing な不整合レコード (例: 本 feature 追加前に作成された BG) を
 *   他フィールドの編集保存で再検証してロックアウトしないよう Edit では
 *   skip する (= Create 時のみ有効)。
 */
// Create / Edit で共通の discriminated union 定義 (skipAreaMismatchCheck に
// 依存しない部分)。 buildBgMasterFormSchema の戻り値型を
// `ReturnType<typeof bgMasterFormSchemaUnion.superRefine>` として明示するために
// 分離する (= .superRefine の呼び出し式そのものを関数の戻り値型注釈に使う)。
const bgMasterFormSchemaUnion = z.discriminatedUnion('licenseType', [
  // 発電 BG: powerType / contractIdNo1 必須
  BaseBgMasterFormSchema.extend({
    bgCode: bgCodeSchema,
    licenseType: z.literal(LicenseType.GENERATION),
    areaCode: z.nativeEnum(AreaCode),
    powerType: z.nativeEnum(PowerType, {
      error: () => '発電 BG の場合は電源種別を選択してください',
    }),
    contractIdNo1: z
      .string()
      .min(1, '発電 BG の場合は契約識別番号1を入力してください')
      .refine(withinRunes(20), '契約識別番号1は20文字以内で入力してください'),
  }),
  // 自己託送 / 小売 / DR: powerType / contractIdNo1 不要
  BaseBgMasterFormSchema.extend({
    bgCode: bgCodeSchema,
    licenseType: z.union([
      z.literal(LicenseType.RETAIL),
      z.literal(LicenseType.SELF_WHEELING),
      z.literal(LicenseType.DR),
    ]),
    areaCode: z.nativeEnum(AreaCode),
  }),
])

// superRefine → refine の 2 段チェーンの戻り値型。 .refine() 追加時に
// ReturnType<typeof bgMasterFormSchemaUnion.superRefine> だけでは型が
// 合わなくなったため、 さらに ['refine'] の戻り値型まで辿って明示する。
type BgMasterFormSchemaAfterSuperRefine = ReturnType<
  typeof bgMasterFormSchemaUnion.superRefine
>
type BgMasterFormSchemaFinal = ReturnType<
  BgMasterFormSchemaAfterSuperRefine['refine']
>

const CROSS_MASTER_DUPLICATE_MESSAGE =
  'このコードは既に登録されています。別のコードを入力してください'

/**
 * AC-OMB-020: member 行の適用期間が BG 本体の適用期間に内包されるか判定する
 * (backend valueobject.ValidPeriod.Contains と同じ判定式、
 * occto-master-bg-member-valid-period-containment 設計書 Decision #4、
 * cross-bound 条項は 2026-08-17 review 補完)。
 *
 * 下端は常に判定する (member.validFrom が bg.validFrom より前なら false)。
 * bg.validTo が Some のとき、 member.validFrom が bg.validTo より後なら
 * member.validTo の有無によらず false とする (= bg が失効した後にしか
 * 始まらない member 行は、 member.validTo が未設定〈無期限〉でも実質空区間
 * で内包不可。 下端 cross-bound チェック)。
 *
 * 上端は次で決まる (下端 cross-bound を通過した前提):
 * bg.validTo が未設定 (無期限) か member.validTo が未設定
 * (Decision #4: 行の無期限は BG 終端に暗黙的に cap される) のとき常に許容し、
 * 両方 Some のときのみ member.validTo <= bg.validTo を要求する。
 */
function isMemberPeriodContainedInBg(
  member: { validFrom: CivilDate; validTo?: CivilDate },
  bg: { validFrom: CivilDate; validTo?: CivilDate },
): boolean {
  if (compareCivilDate(member.validFrom, bg.validFrom) < 0) {
    return false
  }
  if (bg.validTo === undefined) {
    return true
  }
  if (compareCivilDate(member.validFrom, bg.validTo) > 0) {
    return false
  }
  if (member.validTo === undefined) {
    return true
  }
  return compareCivilDate(member.validTo, bg.validTo) <= 0
}

/**
 * AC-OMB-020 の spec verbatim メッセージ (bg.md L144)。 {終了日} は BG 本体
 * ValidTo が未設定 (無期限) のとき「未設定」と表示する (Decision #3、
 * §ビジネスルール「未設定 (null) は『現在も有効』」に整合)。 日付書式は既存
 * FE civilDate 表示ヘルパ (`formatCivilDate`、 一覧の適用開始日/終了日列と
 * 同じ YYYY/MM/DD) に合わせる。
 */
function bgMemberPeriodContainmentMessage(bg: {
  validFrom: CivilDate
  validTo?: CivilDate
}): string {
  const from = formatCivilDate(bg.validFrom)
  const to = bg.validTo === undefined ? '未設定' : formatCivilDate(bg.validTo)
  return `所属事業者の適用期間は BG の適用期間 (${from}〜${to}) 内で入力してください`
}

/**
 * AC-OMB-022 の spec verbatim メッセージ (bg.md L162)。
 * bgMemberPeriodContainmentMessage と同じ日付書式規約を使う。
 */
function bgRepMemberPeriodMismatchMessage(bg: {
  validFrom: CivilDate
  validTo?: CivilDate
}): string {
  const from = formatCivilDate(bg.validFrom)
  const to = bg.validTo === undefined ? '未設定' : formatCivilDate(bg.validTo)
  return `代表事業者の適用期間は BG の適用期間 (${from}〜${to}) と同じにしてください`
}

export function buildBgMasterFormSchema(
  skipAreaMismatchCheck: boolean,
  existingTradingPartnerCodes: Set<string> = new Set(),
): BgMasterFormSchemaFinal {
  return (
    bgMasterFormSchemaUnion
      .superRefine((data, ctx) => {
        // bgCode 先頭文字と licenseType の整合 (backend と同じルール)。
        // bgCodeSchema を通過した時点で先頭は G/L/D のいずれかが保証される。
        const expectedPrefix = REQUIRED_BG_CODE_PREFIX[data.licenseType]
        if (
          expectedPrefix !== undefined &&
          data.bgCode.length > 0 &&
          data.bgCode[0] !== expectedPrefix
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${licenseTypeLabel(data.licenseType)}の BG コードは「${expectedPrefix}」で始めてください`,
            path: ['bgCode'],
          })
        }
        // AC-BG14: BG コード末尾 1 桁 (エリアコード) と選択中のエリアの不一致検証。
        // areaCodeFromBgCode を自動入力 (AC-BG13) と共有し、判定式の乖離を防ぐ。
        if (!skipAreaMismatchCheck) {
          const expectedAreaCode = areaCodeFromBgCode(data.bgCode)
          if (
            expectedAreaCode !== undefined &&
            expectedAreaCode !== data.areaCode
          ) {
            const trailingDigit = data.bgCode[data.bgCode.length - 1]
            const expectedAreaLabel = convertAreaLabel(expectedAreaCode)
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `BG コード末尾「${trailingDigit}」は ${expectedAreaLabel} を表します。エリアを ${expectedAreaLabel} に変更するか、BG コードを修正してください`,
              path: ['areaCode'],
            })
          }
        }
        // AC-MOI-013 extension: 取引先マスタの account code と重複する BG code は
        // Create 時に frontend で即時 reject (backend 検証は必須)。
        if (
          data.bgCode.length === 5 &&
          existingTradingPartnerCodes.has(data.bgCode)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: CROSS_MASTER_DUPLICATE_MESSAGE,
            path: ['bgCode'],
          })
        }
        // メンバー検証 (is_own≤1 / 代表包含 / 重複不可) は PlantMaster と共通ルール
        addOwnerMemberIssues(ctx, {
          members: data.members,
          repBusinessMasterId: data.repBusinessMasterId,
          memberNoun: '所属事業者',
          repPath: 'repBusinessMasterId',
        })
        // AC-OMB-022: 構成事業者リストの先頭行 (代表事業者) の適用期間は BG
        // 本体の適用期間と完全一致でなければならない (backend
        // validateBgMasterRepMemberPeriod の frontend 即時反映。 一致制約であり
        // 内包では不可 — issue #6038)。 先頭行は AC-OMB-021 により readonly +
        // 自動同期される想定だが、 frontend fail-fast として保存時にも検証する
        // (backend 側の二重防御は entity 側 AC-OMB-022 に別途ある)。
        const repMember = data.members[0]
        if (
          repMember !== undefined &&
          (compareCivilDate(repMember.validFrom, data.validFrom) !== 0 ||
            (repMember.validTo === undefined) !==
              (data.validTo === undefined) ||
            (repMember.validTo !== undefined &&
              data.validTo !== undefined &&
              compareCivilDate(repMember.validTo, data.validTo) !== 0))
        ) {
          const message = bgRepMemberPeriodMismatchMessage(data)
          const validFromMismatched =
            compareCivilDate(repMember.validFrom, data.validFrom) !== 0
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ['members', 0, validFromMismatched ? 'validFrom' : 'validTo'],
          })
        }
        // AC-OMB-020: 構成事業者リストの 2 行目以降 (代表事業者を除く) の適用
        // 期間は BG 本体の適用期間に内包されなければならない (backend
        // validateBgMasterMembersPeriod の frontend 即時反映。 先頭行 (代表) は
        // 上記 AC-OMB-022 の一致制約が優先し本チェックの対象外 — issue #6038)。
        // validFrom に issue を付けるのは (a) 下端違反 (member.validFrom <
        // BG.validFrom) または (b) 下端 cross-bound 違反 (BG.validTo=Some で
        // member.validFrom > BG.validTo。 member.validTo の有無によらず
        // validFrom 自体が範囲外)。 それ以外 (member.validFrom は範囲内だが
        // member.validTo が BG.validTo を超える上端違反) は validTo に付ける
        // (行単体の to>=from エラー〈validPeriodRefine〉と表示先を分け、
        // 同時発生時は両方表示する)。
        for (const [index, member] of data.members.entries()) {
          if (index === 0) {
            continue
          }
          if (isMemberPeriodContainedInBg(member, data)) {
            continue
          }
          const message = bgMemberPeriodContainmentMessage(data)
          const validFromViolated =
            compareCivilDate(member.validFrom, data.validFrom) < 0 ||
            (data.validTo !== undefined &&
              compareCivilDate(member.validFrom, data.validTo) > 0)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: [
              'members',
              index,
              validFromViolated ? 'validFrom' : 'validTo',
            ],
          })
        }
        // BG 区分ゲート (backend validateBgMasterPlants と同じ不変条件の
        // defensive mirror): 発電以外の BG は配下発電所を持てない。
        // BgPlantMembershipSection が区分切替時に自動でクリアするため通常は
        // 到達しないが、 backend の gate を frontend でも mirror する。
        if (
          data.licenseType !== LicenseType.GENERATION &&
          data.plants.length > 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              '配下発電所は発電 BG のみ設定できます。BG 区分を発電に変更するか、配下発電所を削除してください',
            path: ['plants'],
          })
        }
        if (
          data.licenseType !== LicenseType.SELF_WHEELING &&
          data.wheelingLinkCode !== undefined
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '電源特定コードは自己託送 BG のみ設定できます',
            path: ['wheelingLinkCode'],
          })
        }
      })
      // 適用終了日 ≥ 適用開始日 (同日許容、AC-03)。7 タブ共通 refine。
      .refine(validPeriodRefine, validPeriodRefineIssue)
  )
}

/** 新規作成用 schema。 AC-BG14 (bgCode 末尾とareaCodeの不一致検証) を含む。 */
export const BgMasterFormSchema = buildBgMasterFormSchema(false)

/**
 * 編集用 schema。 bgCode / areaCode が readonly な Edit モードでは AC-BG14 を
 * skip する (buildBgMasterFormSchema の JSDoc 参照)。
 */
export const BgMasterEditFormSchema = buildBgMasterFormSchema(true)

export type BgMasterFormValues = z.infer<typeof BgMasterFormSchema>
// Zod v4 では .refine / discriminatedUnion で z.input ≠ z.output になる。
// useForm の defaultValues / Control は narrowing 前 (= 部分的に未確定の値を許容する側)
// を受ける必要があるため Input 型を別出し。 submit handler は出力 (= narrow) を受け取る。
export type BgMasterFormInput = z.input<typeof BgMasterFormSchema>

/**
 * テスト / storybook 用の valid な初期値生成ヘルパー (発電 BG)。
 * 発電 BG の full valid values をデフォルトとし、 overrides で特定フィールドを上書きできる。
 */
export function makeValidFormValues(
  overrides?: Partial<BgMasterFormValues>,
): BgMasterFormValues {
  const repBusinessMasterId = '12345678-1234-4234-8234-123456789012'
  const base: BgMasterFormValues = {
    // bgCode 末尾 1 桁 = '1' → 北海道 (AC-BG14 の cross-field 検証と整合させる。
    // areaCode を bgCode と無関係な値にすると superRefine の不一致検証に
    // 引っかかり、 本来 valid な override テストが軒並み invalid になる)。
    bgCode: 'GA011',
    bgName: 'テスト発電 BG',
    licenseType: LicenseType.GENERATION,
    areaCode: AreaCode.HOKKAIDO,
    powerType: PowerType.NON_ADJUSTABLE,
    repBusinessMasterId,
    members: [
      {
        businessMasterId: repBusinessMasterId,
        isOwn: true,
        validFrom: todayCivilDate(),
        validTo: undefined,
      },
    ],
    plants: [],
    validFrom: todayCivilDate(),
    validTo: undefined,
    contractIdNo1: 'CONTRACT-001',
    note: undefined,
  }
  return { ...base, ...overrides } as BgMasterFormValues
}
